/* import __COLOCATED_TEMPLATE__ from './handoff-to-salesforce-case.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { type SalesforceCaseDescriptionSource } from 'embercom/models/operator/visual-builder/step/handoff-to-salesforce-case';
import type HandoffToSalesforceCaseType from 'embercom/models/operator/visual-builder/step/handoff-to-salesforce-case';
import type HandoffToSalesforceTeamType from 'embercom/models/operator/visual-builder/step/handoff-to-salesforce-team';
import type EditorState from 'embercom/objects/workflows/graph-editor/editor-state';
import type IntlService from 'ember-intl/services/intl';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { action } from '@ember/object';
import { BlocksDocument } from '@intercom/composer-ember';
import createFragmentFromBlock from 'embercom/lib/blocks/create-fragment-from-block';
import { type Block } from '@intercom/interblocks.ts';
import type Store from '@ember-data/store';

export interface HandoffToSalesforceCaseArgs {
  step: HandoffToSalesforceCaseType | HandoffToSalesforceTeamType;
  editorState: EditorState;
  readOnly: boolean;
  isTemplatePreview?: boolean;
  settingsBannerHeading?: string;
}

export interface HandoffOption {
  text: string;
  value: string | number | null;
  isDisabled?: boolean;
}

export interface HandoffOptionGroup {
  heading: string;
  items: HandoffOption[];
}

export default class HandoffToSalesforceCase extends Component<HandoffToSalesforceCaseArgs> {
  @service declare intl: IntlService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare store: Store;

  get salesforcePlatform() {
    return this.finStandaloneService.salesforcePlatform;
  }

  get salesforceConfig() {
    return this.salesforcePlatform.salesforceConfig!;
  }

  get salesforceSetupData() {
    return this.salesforcePlatform.salesforceSetupData;
  }

  get salesforceConfigurationData() {
    return this.salesforcePlatform.configurationData;
  }

  get isLoading() {
    return (
      this.salesforcePlatform.salesforceDataLoader.isLoadingSalesforceSetupData ||
      this.salesforcePlatform.salesforceDataLoader.isLoadingSalesforceConfig
    );
  }

  get salesforceCaseDescriptionSource() {
    if (this.isHandoffToSalesforceCase) {
      return (this.args.step as HandoffToSalesforceCaseType).salesforceCaseDescriptionSource;
    }

    return;
  }

  get selectedHandoverOptions(): string | undefined {
    return this.salesforcePlatform.configurations
      ?.map((integration) => {
        let ownerForIntegration =
          this.args.step?.handoffToSalesforceOwnerIdByIntegration?.find(
            (owner) => owner.integration_id === integration.id,
          )?.handoff_id || this.args.step?.handoffToSalesforceOwnerId;

        if (!ownerForIntegration) {
          return '';
        }

        let handoverOption = integration.findHandoverOptionById(ownerForIntegration);
        if (handoverOption) {
          return handoverOption.name;
        }

        return this.intl.t(
          'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.unknown-user-or-queue',
        );
      })
      .join(', ');
  }

  get isHandoffToSalesforceCase(): boolean {
    // we use this component for handoff to salesforce team as well
    return !!this.args.step.typeKey?.endsWith('handoff-to-salesforce-case');
  }

  get settingsBannerHeading(): string {
    return (
      this.args.settingsBannerHeading ||
      this.intl.t(
        'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.banner-heading',
      )
    );
  }

  get salesforceCaseSubjectBlocks(): BlocksDocument {
    let blocks = this.caseSubjectBlocksForStep;

    if (this.shouldShowDefaultBlocks(blocks)) {
      blocks = new BlocksDocument([
        createFragmentFromBlock(this.store, {
          type: 'paragraph',
          // eslint-disable-next-line @intercom/intercom/no-bare-strings
          text: 'Case from Fin AI Agent with {{full_name}}',
        }),
      ]);
    }
    return blocks;
  }

  private get caseSubjectBlocksForStep() {
    if (this.isHandoffToSalesforceCase) {
      return new BlocksDocument(
        (this.args.step as HandoffToSalesforceCaseType).salesforceCaseSubjectBlocks?.toArray() ||
          [],
      );
    }

    return new BlocksDocument([]);
  }

  get salesforceCaseDescriptionBlocks(): BlocksDocument {
    let blocks = this.caseDescriptionBlocksForStep;

    if (this.shouldShowDefaultBlocks(blocks)) {
      blocks = new BlocksDocument([
        createFragmentFromBlock(this.store, {
          type: 'paragraph',
          // eslint-disable-next-line @intercom/intercom/no-bare-strings
          text: 'Created from Fin AI Agent',
        }),
      ]);
    }
    return blocks;
  }

  private get caseDescriptionBlocksForStep() {
    if (this.isHandoffToSalesforceCase) {
      return new BlocksDocument(
        (
          this.args.step as HandoffToSalesforceCaseType
        ).salesforceCaseDescriptionBlocks?.toArray() || [],
      );
    }

    return new BlocksDocument([]);
  }

  private shouldShowDefaultBlocks(blocks: BlocksDocument) {
    if (blocks.blocks.length === 0) {
      return true;
    }

    if (blocks.blocks.length === 1) {
      let block = blocks.blocks[0];
      return 'text' in block && typeof block.text === 'string' && block.text.trim() === '';
    }

    return false;
  }

  @action
  setCaseDescriptionSource(source: SalesforceCaseDescriptionSource) {
    if (this.isHandoffToSalesforceCase) {
      (this.args.step as HandoffToSalesforceCaseType).salesforceCaseDescriptionSource = source;
    }
  }

  @action
  onCaseSubjectChange(blockDocuments: BlocksDocument) {
    if (this.isHandoffToSalesforceCase) {
      let blockFragments = blockDocuments.blocks.map((block: Block) =>
        createFragmentFromBlock(this.store, block),
      );

      let step = this.args.step as HandoffToSalesforceCaseType;

      step.salesforceCaseSubjectBlocks?.clear();
      step.salesforceCaseSubjectBlocks?.pushObjects(blockFragments);
    }
  }

  @action
  onCaseDescriptionChange(blockDocuments: BlocksDocument) {
    if (this.isHandoffToSalesforceCase) {
      let blockFragments = blockDocuments.blocks.map((block: Block) =>
        createFragmentFromBlock(this.store, block),
      );

      let step = this.args.step as HandoffToSalesforceCaseType;

      step.salesforceCaseDescriptionBlocks?.clear();
      step.salesforceCaseDescriptionBlocks?.pushObjects(blockFragments);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::HandoffToSalesforceCase': typeof HandoffToSalesforceCase;
    'workflows/graph-editor/node-items/steps/handoff-to-salesforce-case': typeof HandoffToSalesforceCase;
  }
}
